import React from 'react';
import { Divider } from 'antd';
import { ReadOnlyItem } from '../data-display/ReadOnlyItem/ReadOnlyItem';
import { Row } from '../layout/Flex';
import { Text } from '../typography/Typography/Typography';

interface EntityIdProps {
  id: number | string | null;
  systemId: number | null;
  code?: string | null;
  /** Label for the system ID field. Defaults to "Y'Id" */
  systemIdLabel?: string;
  /** Optional callback invoked after text is successfully copied when copyOnClick is true */
  onCopySuccess?: (text: string) => void;
  /** Optional callback invoked when copying fails when copyOnClick is true */
  onCopyError?: (text: string, error: Error) => void;
}

interface EntityItemProps {
  label: string;
  value: number | string;
  /** Optional callback invoked after text is successfully copied when copyOnClick is true */
  onCopySuccess?: (text: string) => void;
  /** Optional callback invoked when copying fails when copyOnClick is true */
  onCopyError?: (text: string, error: Error) => void;
}

export const EntityItem: React.FC<EntityItemProps> = React.memo(
  ({ label, value, onCopyError, onCopySuccess }) => {
    return (
      <ReadOnlyItem
        label={label}
        value={value}
        copyOnClick
        spaceBetween="0.2rem"
        plainLabel
        onCopySuccess={onCopySuccess}
        onCopyError={onCopyError}
      />
    );
  },
);

EntityItem.displayName = 'EntityItem';

/**
 * Common way to render an id and code of an entity like Category, Product, Store etc.
 * ID and Code are rendered in square brackets with an "ID: " and "Code: " prefixes.
 * When the value is clicked, it is copied to the clipboard.
 * @param id
 * @param code
 * @param systemId
 * @param systemIdLabel Label for the system ID field. Defaults to "Y'Id"
 * @param onCopySuccess callback invoked after text is successfully copied
 * @param onCopyError callback invoked when copying fails
 */
export const EntityId: React.FC<EntityIdProps> = ({
  id,
  code,
  systemId,
  systemIdLabel = "Y'Id",
  onCopySuccess,
  onCopyError,
}) => {
  if (id || code || systemId) {
    return (
      <Row $gap="2px" $alignItems="center">
        <Text>[</Text>
        {id && (
          <EntityItem
            label="Id"
            value={id}
            onCopySuccess={onCopySuccess}
            onCopyError={onCopyError}
          />
        )}
        {id && code && <Divider type="vertical" />}
        {code && (
          <EntityItem
            label="Code"
            value={code}
            onCopySuccess={onCopySuccess}
            onCopyError={onCopyError}
          />
        )}
        {systemId && (code ?? id) && <Divider type="vertical" />}
        {systemId && (
          <EntityItem
            label={systemIdLabel}
            value={systemId}
            onCopySuccess={onCopySuccess}
            onCopyError={onCopyError}
          />
        )}
        <Text>]</Text>
      </Row>
    );
  }
};
